// The Ogma colour system as data (design system v2, 12 Oct 2026: warm paper, warm ink, a softer seal red used as a mark,
// an ink spine): six themeable inputs (background, foreground, highlight, action, reminder and the navigation spine),
// two fixed colours (done, danger), and every other token derived from them with colour mixes. The CSS
// custom properties in styles.css between the THEME markers are generated from this file (themeCss()); a test checks
// they agree and prints the expected block when they do not, and another checks every text pair meets WCAG contrast in
// both themes. A tenant's brand colour may feed
// highlight and action only (brand.ts), never reminder, done or danger.

export type ThemeName = "light" | "dark";

export interface Inputs {
  background: string;
  foreground: string;
  highlight: string;
  /** Usually the highlight; a white-label brand with a pale colour can split it out. */
  action: string;
  reminder: string;
  /** The navigation spine (left-hand bar): warm ink for Ogma; a white-label business may set its own (brand.ts). */
  spine: string;
}

/** Bone: text and marks on the spine. */
export const BONE = "#f7f3ea";

/** Warm ink: the text colour that goes on pale fills (white or ink, whichever reads). */
export const INK = "#1d1b18";

export const INPUTS: Record<ThemeName, Inputs> = {
  light: { background: "#f3efe6", foreground: INK, highlight: "#9b3340", action: "#9b3340", reminder: "#b07a22", spine: INK },
  dark: { background: "#1b1916", foreground: "#ece6da", highlight: "#e07f88", action: "#e07f88", reminder: "#d4a24a", spine: "#141210" },
};

/** Fixed, outside the theme: never a brand colour. */
export const FIXED: Record<ThemeName, { done: string; danger: string }> = {
  light: { done: "#2e5e3e", danger: "#b3261e" },
  dark: { done: "#8fc29e", danger: "#f2b8b5" },
};

/** A derived token: `a` mixed with `b` (each a token name or a hex colour), `pct` percent of `a`. */
interface Mix {
  a: string;
  pct: number;
  b: string;
}

export const DERIVED: Record<ThemeName, Record<string, Mix | string>> = {
  light: {
    surface: { a: "background", pct: 32, b: "#ffffff" },
    muted: { a: "foreground", pct: 72, b: "background" },
    rule: { a: "foreground", pct: 17, b: "background" },
    "rule-soft": { a: "foreground", pct: 5, b: "background" },
    "reminder-soft": { a: "reminder", pct: 18, b: "#ffffff" },
    "on-reminder": { a: "reminder", pct: 34, b: "#000000" },
    // An amber fill always takes ink, in both themes (DS: reminder as a fill takes foreground-dark text).
    "on-reminder-fill": INK,
    "highlight-soft": { a: "highlight", pct: 8, b: "#ffffff" },
    // The red margin rule of a ruled pad: one 1px line down the left of the main panel (decorative).
    margin: { a: "highlight", pct: 52, b: "background" },
    // The spine is warm ink with bone on it; the active notch is the light seal red, the only red on the bar. A
    // white-label spine colour flows through the same mixes, and its notch is bone or ink instead (brand.ts sets
    // --brand-spine-highlight), since red may not read on the business's colour.
    "on-spine-muted": { a: "on-spine", pct: 68, b: "spine" },
    "spine-rule": { a: "on-spine", pct: 22, b: "spine" },
    "spine-highlight": "highlight-on-dark",
  },
  dark: {
    // Kept close to the ground so seal-red text still reads at 4.5:1 on panels.
    surface: { a: "foreground", pct: 2.5, b: "background" },
    muted: { a: "foreground", pct: 70, b: "background" },
    rule: { a: "foreground", pct: 18, b: "background" },
    "rule-soft": { a: "foreground", pct: 8, b: "background" },
    "reminder-soft": { a: "reminder", pct: 22, b: "background" },
    "on-reminder": { a: "reminder", pct: 30, b: "#ffffff" },
    "on-reminder-fill": INK,
    "highlight-soft": { a: "highlight", pct: 9, b: "background" },
    margin: { a: "highlight", pct: 40, b: "background" },
    "on-spine-muted": { a: "foreground", pct: 70, b: "background" },
    "spine-rule": { a: "foreground", pct: 14, b: "background" },
    "spine-highlight": "highlight-on-dark",
  },
};

// ---------------------------------------------------------------------------
// Colour maths (sRGB, the same as CSS color-mix(in srgb, …))
// ---------------------------------------------------------------------------
export function hexToRgb(hex: string): [number, number, number] {
  let h = hex.trim().replace(/^#/, "");
  if (h.length === 3) h = h.split("").map((c) => c + c).join("");
  return [parseInt(h.slice(0, 2), 16), parseInt(h.slice(2, 4), 16), parseInt(h.slice(4, 6), 16)];
}

export function rgbToHex([r, g, b]: [number, number, number]): string {
  return "#" + [r, g, b].map((v) => Math.round(Math.max(0, Math.min(255, v))).toString(16).padStart(2, "0")).join("");
}

export function mix(a: string, pct: number, b: string): string {
  const x = hexToRgb(a);
  const y = hexToRgb(b);
  const k = pct / 100;
  return rgbToHex([x[0] * k + y[0] * (1 - k), x[1] * k + y[1] * (1 - k), x[2] * k + y[2] * (1 - k)]);
}

function luminance(hex: string): number {
  const ch = (c: number) => {
    const s = c / 255;
    return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
  };
  const [r, g, b] = hexToRgb(hex);
  return 0.2126 * ch(r) + 0.7152 * ch(g) + 0.0722 * ch(b);
}

export function contrastRatio(a: string, b: string): number {
  const [l1, l2] = [luminance(a), luminance(b)].sort((p, q) => q - p) as [number, number];
  return (l1 + 0.05) / (l2 + 0.05);
}

/** White or ink, whichever reads better on a fill (DS: on-action flips to keep 4.5:1). */
export function onColour(fill: string, ink = INK): string {
  return contrastRatio("#ffffff", fill) >= contrastRatio(ink, fill) ? "#ffffff" : ink;
}

/** Bone on the spine when it reads (4.5:1), else ink: a pale white-label spine takes dark text. */
export function onSpine(spine: string): string {
  return contrastRatio(BONE, spine) >= 4.5 ? BONE : INK;
}

/** Every token of a theme, as hex, for the given inputs. */
export function resolveTheme(name: ThemeName, inputs: Inputs = INPUTS[name]): Record<string, string> {
  const t: Record<string, string> = { ...inputs, ...FIXED[name] };
  t["on-spine"] = onSpine(inputs.spine);
  t["highlight-on-dark"] = INPUTS.dark.highlight;
  const val = (ref: string) => (ref.startsWith("#") ? ref : t[ref]!);
  for (const [k, d] of Object.entries(DERIVED[name])) t[k] = typeof d === "string" ? val(d) : mix(val(d.a), d.pct, val(d.b));
  t["on-action"] = onColour(t.action!);
  delete t["highlight-on-dark"];
  return t;
}

// ---------------------------------------------------------------------------
// The CSS
// ---------------------------------------------------------------------------
function cssValue(d: Mix | string): string {
  const ref = (r: string) => (r.startsWith("#") ? r : r === "highlight-on-dark" ? "var(--ogma-highlight-dark)" : `var(--${r})`);
  return typeof d === "string" ? ref(d) : `color-mix(in srgb, ${ref(d.a)} ${d.pct}%, ${ref(d.b)})`;
}

function block(name: ThemeName, indent: string): string {
  const lines: string[] = [];
  const i = INPUTS[name];
  // Highlight and action can come from the business's brand (brand.ts sets --brand-*); the rest cannot.
  lines.push(`--background: ${i.background};`);
  lines.push(`--foreground: ${i.foreground};`);
  lines.push(`--highlight: var(--brand-highlight-${name}, ${i.highlight});`);
  lines.push(`--action: var(--brand-action-${name}, ${i.action});`);
  lines.push(`--on-action: var(--brand-on-action-${name}, ${onColour(i.action)});`);
  lines.push(`--reminder: ${i.reminder};`);
  // The spine can come from a white-label business (brand.ts sets --brand-spine and --brand-on-spine).
  lines.push(`--spine: var(--brand-spine, ${i.spine});`);
  lines.push(`--on-spine: var(--brand-on-spine, ${onSpine(i.spine)});`);
  lines.push(`--done: ${FIXED[name].done};`);
  lines.push(`--danger: ${FIXED[name].danger};`);
  for (const [k, d] of Object.entries(DERIVED[name]))
    lines.push(k === "spine-highlight" ? `--${k}: var(--brand-spine-highlight, ${cssValue(d)});` : `--${k}: ${cssValue(d)};`);
  return lines.map((l) => indent + l).join("\n");
}

/**
 * The generated block in styles.css. Light (warm paper) by default, whatever the device setting: Ogma is an off-white
 * product. Dark is opt-in from Personal settings (lib/themePref.ts sets data-theme to "dark", or "device" to follow
 * a dark device).
 */
export function themeCss(): string {
  return [
    "/* THEME:BEGIN (generated from src/lib/theme.ts by themeCss(); edit there, a test checks they agree) */",
    ":root {",
    `  --ogma-highlight-dark: var(--brand-highlight-dark, ${INPUTS.dark.highlight});`,
    block("light", "  "),
    "  color-scheme: light;",
    "}",
    "/* Dark is opt-in (Personal settings): chosen outright, or following a dark device. */",
    ':root[data-theme="dark"] {',
    block("dark", "  "),
    "  color-scheme: dark;",
    "}",
    "@media (prefers-color-scheme: dark) {",
    '  :root[data-theme="device"] {',
    block("dark", "    "),
    "    color-scheme: dark;",
    "  }",
    "}",
    "/* THEME:END */",
  ].join("\n");
}

/** The text and graphic pairs that must stay legible, with the ratio each needs. */
export const CONTRAST_PAIRS: { fg: string; bg: string; min: number; what: string }[] = [
  { fg: "foreground", bg: "background", min: 4.5, what: "body text" },
  { fg: "foreground", bg: "surface", min: 4.5, what: "text on panels" },
  { fg: "foreground", bg: "rule-soft", min: 4.5, what: "text on a hovered row" },
  { fg: "foreground", bg: "highlight-soft", min: 4.5, what: "text in a row that needs you" },
  { fg: "foreground", bg: "reminder-soft", min: 4.5, what: "text in an approval row" },
  { fg: "muted", bg: "background", min: 4.5, what: "secondary text" },
  { fg: "muted", bg: "surface", min: 4.5, what: "secondary text on panels" },
  { fg: "highlight", bg: "background", min: 4.5, what: "links and stamps" },
  { fg: "highlight", bg: "surface", min: 4.5, what: "links and stamps on panels" },
  { fg: "on-action", bg: "action", min: 4.5, what: "the action button" },
  { fg: "on-reminder", bg: "reminder-soft", min: 4.5, what: "for-approval tags" },
  { fg: "on-reminder-fill", bg: "reminder", min: 4.5, what: "text on an amber fill (counts, the demo tag)" },
  { fg: "done", bg: "surface", min: 4.5, what: "sent and booked tags" },
  { fg: "done", bg: "background", min: 4.5, what: "done ticks" },
  { fg: "danger", bg: "surface", min: 4.5, what: "errors on panels" },
  { fg: "danger", bg: "background", min: 4.5, what: "errors" },
  { fg: "on-spine", bg: "spine", min: 4.5, what: "navigation labels" },
  { fg: "on-spine-muted", bg: "spine", min: 4.5, what: "inactive navigation labels" },
  { fg: "spine-highlight", bg: "spine", min: 3, what: "the active navigation notch" },
  { fg: "margin", bg: "background", min: 1.5, what: "the margin rule (decorative)" },
  { fg: "highlight", bg: "highlight-soft", min: 4.5, what: "next-step text in a row that needs you" },
  { fg: "rule", bg: "surface", min: 1.2, what: "dividers (decorative)" },
  { fg: "muted", bg: "highlight-soft", min: 4.5, what: "secondary text in a row that needs you" },
];
